
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { useMainDict } from "@/libs/hooks/useTranslation";
import { IconArrowLeft, IconExclamationCircle, IconShieldLock } from "@tabler/icons-react";
import { GetStaticProps } from "next";
import Link from "next/link";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import {
  preFetchPrivacyPolicy,
  usePrivacyPolicy,
} from "@/libs/hooks/usePrivacyPolicy";

export default function PrivacyPolicy() {
  const { t } = useMainDict();
  const { privacyPolicy, isLoading } = usePrivacyPolicy();

  if (!privacyPolicy?.id) {
    return (
      <div className="relative bg-gradient-to-b from-vintage-burgundy via-[#5a252b] to-vintage-espresso w-full min-h-screen font-serif flex items-center justify-center flex-col px-4">
        {/* Background texture */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative z-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full border-2 border-vintage-ochre/50 flex items-center justify-center">
            <IconExclamationCircle className="text-vintage-ochre" size={40} />
          </div>
          <h1 className="font-display text-3xl text-vintage-cream mb-4">
            {t("somethingWentWrong")}
          </h1>
          <Link
            href="/"
            aria-label="Go back home"
            className="inline-flex items-center gap-2 vintage-btn vintage-btn-primary"
          >
            <IconArrowLeft size={18} />
            <span>{t("goBackHome") || "Return Home"}</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-b from-vintage-parchment via-vintage-cream to-vintage-parchment">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-burgundy to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">
        {/* Back button and header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            href="/"
            className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-vintage-burgundy text-vintage-burgundy hover:bg-vintage-burgundy hover:text-vintage-cream transition-all duration-300"
            aria-label="Go back home"
          >
            <IconArrowLeft size={20} />
          </Link>
          <div className="flex-1" />
        </div>

        {/* Main content card */}
        <div className="relative bg-vintage-cream/95 rounded-sm shadow-vintage p-8 md:p-12">
          {/* Inner border */}
          <div className="absolute inset-3 border border-vintage-walnut/10 pointer-events-none" />

          {/* Corner decorations */}
          <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-vintage-ochre/40" />
          <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-vintage-ochre/40" />
          <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-vintage-ochre/40" />
          <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-vintage-ochre/40" />

          {/* Header with icon */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-vintage-burgundy/10 border border-vintage-burgundy/30 mb-4">
              <IconShieldLock size={32} className="text-vintage-burgundy" />
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-vintage-espresso mb-4">
              {privacyPolicy.attributes.title}
            </h1>
            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-3">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
              <span className="text-vintage-ochre">✦</span>
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre to-transparent" />
            </div>
          </div>

          {/* Content */}
          <div className="font-serif text-vintage-walnut leading-relaxed text-justify prose-vintage">
            <BlocksRenderer
              blocks={{
                paragraph: ({ children }) => (
                  <p className="mb-4 text-base leading-relaxed">{children}</p>
                ),
                "list-item": ({ children }) => (
                  <li className="list-disc list-inside ml-4 mb-2">{children}</li>
                ),
                heading: ({ children, level }) => {
                  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
                  return (
                    <Tag className="font-display font-bold text-vintage-espresso mt-8 mb-4">
                      {children}
                    </Tag>
                  );
                },
              }}
              content={privacyPolicy.attributes.text}
            />
          </div>

          {/* Links section */}
          {privacyPolicy.attributes.links && privacyPolicy.attributes.links.length > 0 && (
            <div className="mt-10 pt-8 border-t border-vintage-walnut/20">
              <h2 className="font-display font-bold text-xl text-vintage-espresso mb-4">
                Links
              </h2>
              <ul className="space-y-2">
                {privacyPolicy.attributes.links.map((link, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-vintage-terracotta rotate-45" />
                    <Link
                      className="font-serif text-vintage-burgundy hover:text-vintage-terracotta underline underline-offset-2 decoration-vintage-ochre/50 hover:decoration-vintage-terracotta transition-colors"
                      href={link.href}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bottom decoration */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <div className="w-8 h-px bg-vintage-walnut/30" />
          <div className="w-2 h-2 bg-vintage-ochre/50 rotate-45" />
          <div className="w-8 h-px bg-vintage-walnut/30" />
        </div>
      </div>

      {/* Corner ornaments */}
      <div className="absolute bottom-6 left-6 w-12 h-12 border-l border-b border-vintage-burgundy/20 hidden md:block" />
      <div className="absolute bottom-6 right-6 w-12 h-12 border-r border-b border-vintage-burgundy/20 hidden md:block" />
    </div>
  );
}

 const _getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;
  const privacyPolicy = await preFetchPrivacyPolicy(locale || "en");

  const fallback = {
    ...privacyPolicy,
  };

  return {
    props: {
      fallback,
    },
  };
};


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/privacy-policy/index',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  